import moment from 'moment';

export const UtilsMixin = {
  methods: {
    // GENERALES
    // Método para detectar si el usuario está en un dispositivo móvil
    isMobileDevice() {
      return /Mobi|Android|iPhone|iPad|iPod/.test(navigator.userAgent);
    },
    // Da formato a un número, y redondea a un decimal si se pasa por parámetro
    formatNumber(number, round) {
      if (typeof number !== 'number') {
        return null;
      }
      if (!Number.isInteger(number) && round) {
        number = Math.round(number * 10) / 10;
      }
      return number.toLocaleString('de-DE');
    },
    // Formatea un número largo, como el volumen por ejemplo, para mostrarlo de forma más resumida
    formatLargeNumber(num) {
      if (num >= 1000000) {
        return (num % 1000000 === 0) ? (num / 1000000) + 'M' : (num / 1000000).toFixed(1) + 'M';
      } else if (num >= 1000) {
        return (num % 1000 === 0) ? (num / 1000) + 'K' : (num / 1000).toFixed(1) + 'K';
      } else {
        return num.toString();
      }
    },
    // Formatea una fecha como dd/MM/yyyy
    formatDate(date) {
      const [year, month, day] = date.split("-").map(Number);
      const dateObject = new Date(year, month - 1, day);
      return moment(dateObject).format("DD/MM/YYYY");
    },
    // Método para mostrar mensaje de error por defecto
    showDefaultErrorModal(modalCode) {
      this.$store.dispatch('modal/showModal', {
        id: modalCode,
        payload: {
          title: this.$t('general.error'), 
          text: this.$t('general.default.errorMessage'), 
          confirmButtonText: this.$t('general.ok'), 
        },
      });
    },

    // TABLAS
    // Permite manejar si se expande o se contrae el detalle de una fila de la tabla 
    toggleExpandTable(list, item) {
      if (list[0] === item) {
        list.splice(0, 1);
      } else {
        if (list.length > 0) {
          list.splice(0, 1);
        }
        list.push(item);
      }
    },

    // DATE PICKERS
    // Obtiene el formato para mostrar en el textfiel del datepicker
    datePickerformatDate(dates) {
      if (!dates || dates.length == 0) return null;
      if (dates.length == 1) {
        const [year, month, day] = dates[0].split('-');
        return `${day}/${month}/${year}`;
      }
      if (dates.length == 2) {
        const [year1, month1, day1] = dates[0].split('-');
        const [year2, month2, day2] = dates[1].split('-');
        return `${day1}/${month1}/${year1} - ${day2}/${month2}/${year2}`;
      }
    },
    // Obtiene el formato para manejar las fechas dentro del calendario del datepicker
    datePickerparseDate (dates) {
      if (!dates || dates.length == 0) return null;
      let datesList = dates.split(' - ')
      if (datesList.length == 1) {
        const [day, month, year] = datesList[0].split('/');
        return [`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`];
      }
      if (datesList.length == 2) {
        const [day1, month1, year1] = datesList[0].split('/');
        const [day2, month2, year2] = datesList[1].split('/');
        return [`${year1}-${month1.padStart(2, '0')}-${day1.padStart(2, '0')}`, `${year2}-${month2.padStart(2, '0')}-${day2.padStart(2, '0')}`];
      }
    },

    // ACCIONES
    // Permite obtener el total de días entre dos fechas
    countDaysBetweenDates(signalDate, closeDate) {
      const signalDateSplited = signalDate.split('/');
      const closeDateSplited = closeDate.split('/');
      
      const signalDateObject = new Date(signalDateSplited[2], signalDateSplited[1] - 1, signalDateSplited[0]);
      const closeDateObject = new Date(closeDateSplited[2], closeDateSplited[1] - 1, closeDateSplited[0]);

      const timeDiff = closeDateObject - signalDateObject;

      return Math.ceil(timeDiff / (1000 * 60 * 60 * 24));
    },
    // Calcula el porcentaje de volumen actual frente a la señal de volumen esperada
    calculateVolumeCompletion(volume, signalVolume) {
      if (volume <= 0) {
        return 0;
      }
      if (signalVolume <= 0) {
        return null;
      }
      return (volume / signalVolume) * 100;
    },
    // Calcula el porcentaje del precio actual frente a la señal de precio esperada y devuelve el resultado formateado
    calculateGrowthPercentageFormatted(currentPrice, targetPrice, isPendingGrowth = false) {
      if (!currentPrice || !targetPrice || targetPrice === 0) {
        return null;
      }
      
      return this.formatNumber(this.calculateGrowthPercentage(currentPrice, targetPrice, isPendingGrowth), true);
    },
    // Calcula el porcentaje del precio actual frente a la señal de precio esperada
    calculateGrowthPercentage(currentPrice, targetPrice, isPendingGrowth = false) {
      if (isPendingGrowth) {
        // Calcular el porcentaje de crecimiento pendiente
        let result = ((targetPrice - currentPrice) / currentPrice) * 100;
        return result >= 0 ? result : 0;
      } else {
        // Calcular el porcentaje de crecimiento ya realizado
        return ((currentPrice - targetPrice) / targetPrice) * 100;
      }
    },
    // Devuelve el color del contenedor asociado al sentimiento de mercado o de acción
    getSentimentColor(value) {
      if (value >= 80) {
        return 'success';
      } else if (value >= 50) {
        return 'warning';
      }
      return 'secondary';
    },
    // Devuelve el color del contenedor asociado al estado general de las señales
    getStatusColor() {
      return '#9E9E9E';
    },
    // Devuelve el color del contenedor asociado a los indicadores propios de la aplicación
    getCustomIndicatorsColor(value) {
      if (value >= 80) {
        return 'success';
      } else if (value >= 50) {
        return 'warning';
      }
      return 'secondary';
    },
    // Devuelve el color del contenedor asociado al contenedor del RSI
    getRsiColor(value) {
      if (value >= 65) {
        return 'success';
      } else if (value >= 35) {
        return 'warning';
      }
      return 'secondary';
    },
    // Devuelve el color del contenedor asociado al contenedor del MACD
    getMacdColor(value) {
      if (value > 0) {
        return 'success';
      } else if (value < 0) {
      return 'secondary';
        }
      return 'warning';
    },
  },
};
